import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DataQuality } from '@/application/metrics';
import { getDataQuality } from '@/infrastructure/http/metricsApi';
import { useAuth } from '@/presentation/contexts/useAuth';
import { MetricBlock } from './MetricBlock';

interface DataQualityBlockProps {
  tenantId: string;
  year: number;
  month: number;
}

/** Alertas de calidad de datos de la clínica seleccionada (caveats §5). Se auto-carga. */
export function DataQualityBlock({ tenantId, year, month }: DataQualityBlockProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<DataQuality | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId || !year || !month) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    getDataQuality(token, { tenantId, year, month })
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, t]);

  const warnings = data?.checks.filter((check) => check.status === 'warning') ?? [];

  return (
    <MetricBlock
      title={t('metrics.dataQuality.title')}
      description={t('metrics.dataQuality.description')}
      isLoading={isLoading}
      error={error}
      isEmpty={!data}
      emptyMessage={t('metrics.states.empty.message')}
    >
      {data && (
        <div className="space-y-2">
          {warnings.length === 0 ? (
            <p className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-800 dark:bg-teal-900/30 dark:text-teal-200">
              {t('metrics.dataQuality.ok')}
            </p>
          ) : (
            warnings.map((check) => (
              <p
                key={check.key}
                role="alert"
                className="flex gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
              >
                <span aria-hidden="true">⚠️</span>
                <span>{t(`metrics.dataQuality.checks.${check.key}`, check.values)}</span>
              </p>
            ))
          )}
        </div>
      )}
    </MetricBlock>
  );
}
